import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Card,
  CardContent,
  Grid,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  IconButton,
  Collapse,
  Chip,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { collection, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import AmbulanceCard from './AmbulanceCard';
import { useNavigate } from 'react-router-dom';  // Changed to useNavigate
import './Reports.css';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Papa from 'papaparse';
import MenuIcon from '@mui/icons-material/Menu'; // Import an icon for toggling
import Confetti from 'react-confetti'; // Import the confetti library
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';



const groupCallsByMonth = (calls) => {
  return calls.reduce((acc, call) => {
    const date = parseDate(call.Date);
    if (!date) return acc;
    const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(call);
    return acc;
  }, {});
};

// Helper function to parse dates correctly, avoiding timezone issues
const parseDate = (date) => {
  if (!date) return null;

  let d;
  if (date instanceof Timestamp) {
    d = date.toDate();
  } else if (date instanceof Date) {
    d = date;
  } else if (typeof date === 'string') {
    // Handle YYYY-MM-DD format strings to avoid timezone issues
    // Parse in local timezone to preserve the date
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      d = new Date(year, month - 1, day);
    } else {
      d = new Date(date);
    }
  } else {
    console.error('Unexpected date format:', date);
    return null;
  }

  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (date) => {
  const d = parseDate(date);
  if (!d) return 'N/A';

  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const TopResponderCard = ({ rank, user }) => (
  <Card sx={{ minWidth: 275, textAlign: 'center', '&:hover': { boxShadow: 6 } }}>
    <CardContent>
      <Typography variant="h5" component="div">Top Responder {rank}</Typography>
      <Typography sx={{ mb: 1.5 }} color="text.secondary">{user.displayName}</Typography>
      <Typography variant="body2">Calls: {user.calls.length}</Typography>
    </CardContent>
  </Card>
);



const Row = ({ user, isEven }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, backgroundColor: isEven ? '#f5f5f5' : 'inherit' }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">{user.displayName}</TableCell>
        <TableCell align="right">{user.email}</TableCell>
        <TableCell align="right">{user.calls.length}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">User Details</Typography>
              <Table size="small" aria-label="calls">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Call Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.calls.map((call, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatDate(call.Date)}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'text.secondary', 
                            marginLeft: 2,
                            fontStyle: 'italic'
                          }}>
                            Role: {call.Role || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="h6" gutterBottom component="div" sx={{ mt: 2 }}>Skills</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {user.skills.length ? (
                  user.skills.map((skill, index) => <Chip key={index} label={skill} color="primary" variant="outlined" />)
                ) : (
                  <Typography>No skills recorded</Typography>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#ff4081' },
    background: { default: '#f4f6f8' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, color: '#333' },
    body1: { color: '#555' },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.3s',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#e0f7fa',
          color: '#00796b',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderBottom: '1px solid #e0e0e0',
        },
        head: {
          backgroundColor: '#f5f5f5',
          fontWeight: 600,
        },
      },
    },
  },
});

const groupEntriesByMonth = (entries) => {
  return entries.reduce((acc, entry) => {
    const date = parseDate(entry.Date); // Parse date correctly to avoid timezone issues
    if (!date) return acc;
    const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(entry);
    return acc;
  }, {});
};

const parseNumericValue = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getMonthlyMetrics = (entries = []) => {
  let callPoints = 0;
  let skillHours = 0;
  let classesCount = 0;
  let nightTimeBonusApplied = false;

  entries.forEach(entry => {
    const entryType = entry.EntryType || entry.type;
    const isSkill = entryType === 'Skill' || Boolean(entry.SkillName);
    const isClass = entryType === 'Class' || Boolean(entry.ClassName);
    const isVolunteer = entryType === 'Volunteer' || Boolean(entry.Time) || Boolean(entry.Activity);
    const isCall = entryType === 'Call' || (!isSkill && !isClass && !isVolunteer);

    if (isCall) {
      callPoints += 1;
      if (
        entry.Role &&
        entry.Role.toLowerCase() === 'nighttime' &&
        !nightTimeBonusApplied
      ) {
        callPoints += 1; // first nighttime call counts double
        nightTimeBonusApplied = true;
      }
    } else if (isSkill) {
      const hours = parseNumericValue(
        entry.PracticeHours ??
        entry.SkillHours ??
        entry.Hours ??
        entry.HoursPracticed ??
        entry.Duration
      );
      skillHours += hours > 0 ? hours : 0;
    } else if (isClass) {
      classesCount += 1;
    }
  });

  return {
    callPoints,
    skillHours,
    classesCount,
    totalPoints: callPoints + skillHours + classesCount,
  };
};

const getStatusFromMetrics = ({ callPoints, skillHours, classesCount }) => {
  const meetsCallRequirement = callPoints >= 8;
  const hasSkillHour = skillHours >= 1;
  const hasClass = classesCount >= 1;

  if (meetsCallRequirement && hasSkillHour && hasClass) {
    return 'green';
  }
  if (meetsCallRequirement) {
    return 'yellow';
  }
  return 'red';
};

const calculateMonthStatus = (entries) => {
  const metrics = getMonthlyMetrics(entries);
  return getStatusFromMetrics(metrics);
};


const calculateTotalPoints = (entries) => {
  const metrics = getMonthlyMetrics(entries);
  return metrics.totalPoints;
};

const StatCard = ({ title, value, icon }) => (
  <Card sx={{ 
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    p: { xs: 1.5, sm: 2 },
    textAlign: 'center',
    boxShadow: { xs: 2, sm: 3 },
    borderRadius: { xs: 1, sm: 2 },
    transition: 'transform 0.2s',
    '&:hover': {
      transform: { xs: 'none', sm: 'scale(1.02)' },
      boxShadow: { xs: 2, sm: 4 },
    }
  }}>
    <CardContent sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 1, sm: 2 },
      p: { xs: '12px !important', sm: '16px !important' }
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: '100%',
        height: { xs: '40px', sm: '60px' }
      }}>
        {icon}
      </Box>
      <Typography variant="h6" component="div" sx={{ 
        fontWeight: 'bold',
        fontSize: { xs: '0.875rem', sm: '1rem' }
      }}>
        {title}
      </Typography>
      <Typography variant="h4" color="primary" sx={{ 
        fontWeight: 'bold',
        fontSize: { xs: '1.5rem', sm: '2rem' }
      }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const availableYears = ['2022', '2023', '2024', '2025']; // Add more years as needed

const getUserStatusForMonth = (user, month) => {
  const entries = [
    ...user.calls.filter(call => isSameMonth(call.Date, month)),
    ...user.skills.filter(skill => isSameMonth(skill.Date, month)),
    ...user.classes.filter(classEntry => isSameMonth(classEntry.Date, month)),
    ...user.volunteerHours.filter(volunteer => isSameMonth(volunteer.Date, month)),
  ];
  return calculateMonthStatus(entries);
};

const isSameMonth = (entryDate, month) => {
  const entryDateObj = parseDate(entryDate);
  if (!entryDateObj) return false;
  
  const [entryMonth, entryYear] = [entryDateObj.getMonth(), entryDateObj.getFullYear()];
  
  const [targetMonth, targetYear] = month.split(' ');
  const targetMonthIndex = new Date(Date.parse(`${targetMonth} 1, ${targetYear}`)).getMonth(); // Get month index

  return entryMonth === targetMonthIndex && entryYear === parseInt(targetYear, 10);
};

const parseMonthYearToDate = (monthYear) => {
  const parsedDate = new Date(monthYear);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const generateMonthsForYear = (yearString) => {
  if (!yearString) {
    return [];
  }
  const yearNumber = parseInt(yearString, 10);
  if (Number.isNaN(yearNumber)) {
    return [];
  }
  return Array.from({ length: 12 }, (_, idx) =>
    new Date(yearNumber, idx, 1).toLocaleString('default', { month: 'long', year: 'numeric' })
  );
};

const summarizeActivities = (activities = []) => {
  const summary = { calls: 0, skills: 0, classes: 0, volunteer: 0, lastActivity: null };

  activities.forEach(activity => {
    const type = activity.type || activity.EntryType;
    if (type === 'Call') {
      summary.calls += 1;
    } else if (type === 'Skill') {
      summary.skills += 1;
    } else if (type === 'Class') {
      summary.classes += 1;
    } else if (type === 'Volunteer') {
      summary.volunteer += 1;
    }

    const date = parseDate(activity.Date);
    if (date) {
      if (!summary.lastActivity || date > summary.lastActivity) {
        summary.lastActivity = date;
      }
    }
  });

  return summary;
};

const formatMonthYearLabel = (date) =>
  date.toLocaleString('default', { month: 'long', year: 'numeric' });

// New component to display green users


const Reports = () => {
  const [userData, setUserData] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topResponders, setTopResponders] = useState([]);
  const [statistics, setStatistics] = useState({
    totalUsers: 0,
    totalCalls: 0,
    totalSkills: 0,
    totalClasses: 0,
    totalVolunteerHours: 0,
    monthlyData: {},
  });
  const [ambulanceCalls, setAmbulanceCalls] = useState({});
  const [isAdmin, setIsAdmin] = useState(null);
  const navigate = useNavigate();  // Changed to useNavigate
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [callsThisYear, setCallsThisYear] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [openMonths, setOpenMonths] = useState({});
  const [classData, setClassData] = useState({}); // State to hold class data
  const [usersData, setUsersData] = useState({}); // Initialize usersData here
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEntries, setUserEntries] = useState({ calls: [], skills: [], classes: [], volunteerHours: [] }); // State for user entries
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [months, setMonths] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // State for sidebar visibility
  const [classReports, setClassReports] = useState({}); // State to hold class reports
  const [openClasses, setOpenClasses] = useState({}); // State to manage open/close for each class
  const [userDisplayNames, setUserDisplayNames] = useState({}); // State to hold mapping of email to display name
  const [userActivities, setUserActivities] = useState({}); // State to hold user activities grouped by month and email
  const [openUsers, setOpenUsers] = useState({}); // State to manage open/close for each user
  const [sortOrder, setSortOrder] = useState('asc'); // State for sorting order
  const [winner, setWinner] = useState(null); // State to hold the randomly selected winner
  const [snackbarOpen, setSnackbarOpen] = useState(false); // State for Snackbar visibility
  const [showWinner, setShowWinner] = useState(false); // State to control winner display
  const [winnerEmail, setWinnerEmail] = useState(''); // State to hold the winner's email
  const [displayName, setDisplayName] = useState(''); // State to hold the cycling display name
  const [showPieChart, setShowPieChart] = useState(false);
  const [selectedMonthCalls, setSelectedMonthCalls] = useState([]);
  const [selectedEligibilityYear, setSelectedEligibilityYear] = useState(() => new Date().getFullYear().toString());
  const [openEligibilityUsers, setOpenEligibilityUsers] = useState({});
  const [selectedMainReportYear, setSelectedMainReportYear] = useState(() => new Date().getFullYear().toString());

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleEligibilityToggle = (email) => {
    setOpenEligibilityUsers(prev => ({
      ...prev,
      [email]: !prev[email],
    }));
  };

  // Function to toggle the open state for a specific month
  const handleMonthToggle = (monthYear) => {
    setOpenMonths((prev) => ({
      ...prev,
      [monthYear]: !prev[monthYear],
    }));
  };

  // Function to toggle the open state for a specific user within a month
  const handleUserToggle = (month, email) => {
    setOpenUsers((prev) => ({
      ...prev,
      [`${month}-${email}`]: !prev[`${month}-${email}`], // Toggle the open state for the specific user
    }));
  };

  // Function to fetch all user data and generate months list
  const fetchUserDataAndGenerateMonths = async () => {
    try {
      // Fetch user data from Firebase
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersDataTemp = {};

      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        usersDataTemp[userData.Email] = {
          email: userData.Email,
          displayName: userData.DisplayName || 'Unknown User',
          calls: [], // Initialize calls as an empty array
          skills: [], // Initialize skills as an empty array
          classes: [], // Initialize classes as an empty array
          volunteerHours: [], // Initialize volunteer hours as an empty array
        };
      });

      console.log("Users Data:", usersDataTemp); // Log users data

      // Fetch additional data for each user (calls, skills, etc.)
      const callsSnapshot = await getDocs(collection(db, 'hvac', 'CallData', 'Responses'));
      const skillsSnapshot = await getDocs(collection(db, 'hvac', 'SkillData', 'Responses'));
      const classesSnapshot = await getDocs(collection(db, 'hvac', 'ClassData', 'Responses'));
      const volunteerSnapshot = await getDocs(collection(db, 'hvac', 'VolunteerData', 'Responses'));

      // Populate usersDataTemp with calls, skills, classes, and volunteerHours
      callsSnapshot.forEach(doc => {
        const data = doc.data();
        const userEmail = data.Email; // Assuming Email is the field in your call data
        if (usersDataTemp[userEmail]) {
          usersDataTemp[userEmail].calls.push(data);
        }
      });

      skillsSnapshot.forEach(doc => {
        const data = doc.data();
        const userEmail = data.Email; // Assuming Email is the field in your skill data
        if (usersDataTemp[userEmail]) {
          usersDataTemp[userEmail].skills.push(data);
        }
      });

      classesSnapshot.forEach(doc => {
        const data = doc.data();
        const userEmail = data.Email; // Assuming Email is the field in your class data
        if (usersDataTemp[userEmail]) {
          usersDataTemp[userEmail].classes.push(data);
        }
      });

      volunteerSnapshot.forEach(doc => {
        const data = doc.data();
        const userEmail = data.Email; // Assuming Email is the field in your volunteer data
        if (usersDataTemp[userEmail]) {
          usersDataTemp[userEmail].volunteerHours.push(data);
        }
      });

      console.log("Users Data After Fetching Activities:", usersDataTemp); // Log after fetching activities

      setUsersData(usersDataTemp);

      // Generate months list based on available data
      const monthsSet = new Set();
      Object.values(usersDataTemp).forEach(user => {
        user.calls.forEach(call => {
          const callDate = parseDate(call.Date); // Parse date correctly to avoid timezone issues
          if (callDate) {
            monthsSet.add(callDate.toLocaleString('default', { month: 'long', year: 'numeric' }));
          }
        });
        user.skills.forEach(skill => {
          const skillDate = parseDate(skill.Date); // Parse date correctly to avoid timezone issues
          if (skillDate) {
            monthsSet.add(skillDate.toLocaleString('default', { month: 'long', year: 'numeric' }));
          }
        });
        user.classes.forEach(classEntry => {
          const classDate = parseDate(classEntry.Date); // Parse date correctly to avoid timezone issues
          if (classDate) {
            monthsSet.add(classDate.toLocaleString('default', { month: 'long', year: 'numeric' }));
          }
        });
        user.volunteerHours.forEach(volunteer => {
          const volunteerDate = parseDate(volunteer.Date); // Parse date correctly to avoid timezone issues
          if (volunteerDate) {
            monthsSet.add(volunteerDate.toLocaleString('default', { month: 'long', year: 'numeric' }));
          }
        });
      });

      const monthsArray = Array.from(monthsSet);
      console.log("Generated Months:", monthsArray); // Log generated months
      setMonths(monthsArray);

      // Set initial selected month
      if (monthsArray.length > 0) {
        setSelectedMonth(monthsArray[0]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUserDataAndGenerateMonths();
  }, []);

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const role = userDocSnap.data().role;
            setUserRole(role ? role.toLowerCase() : null);
            setIsAdmin(role && role.toLowerCase() === 'admin');
          } else {
            console.log('User not found.');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAllUserData();
    }
  }, [isAdmin]);

  const fetchClassData = async () => {
    try {
      const classSnapshot = await getDocs(collection(db, 'hvac', 'ClassData', 'Responses'));
      const classPointsData = {};
      classSnapshot.forEach(doc => {
        const data = doc.data();
        const email = data.Email;
        const classPoints = data.ClassPoints || 0;

        if (!classPointsData[email]) {
          classPointsData[email] = 0; // Initialize if not present
        }
        classPointsData[email] += classPoints; // Aggregate class points by email
      });
      setClassData(classPointsData); // Store the aggregated class points
    } catch (error) {
      console.error('Error fetching class data:', error);
    }
  };

  useEffect(() => {
    fetchClassData(); // Fetch class data on component mount
  }, []);

  const fetchAllUserData = async () => {
    try {
      setLoading(true);
      const usersDataTemp = {}; // Temporary object to hold user data

      // Fetch users data from the correct path
      const usersSnapshot = await getDocs(collection(db, 'users')); // Ensure this path is correct
      console.log('Users Snapshot:', usersSnapshot); // Debugging line

      if (usersSnapshot.empty) {
        console.log('No users found in the collection.'); // Log if no users are found
        return; // Exit if no users
      }

      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        console.log('User Data:', userData); // Debugging line
        usersDataTemp[userData.Email] = {
          email: userData.Email,
          displayName: userData.displayName || 'Unknown User',
          role: userData.Role || 'N/A',
          calls: [], // Initialize calls as an empty array
          skills: [], // Initialize skills as an empty array
          classPoints: classData[userData.Email] || 0,
        };
      });

      console.log('Users Data Temp:', usersDataTemp); // Debugging line
      setUsersData(usersDataTemp); // Set the usersData state with the fetched data
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const groupedCalls = callsThisYear.reduce((acc, call) => {
    const email = call.email;
    const date = parseDate(call.Date); // Parse date correctly to avoid timezone issues
    if (!date) return acc;
    const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });

    if (!acc[email]) {
      acc[email] = {};
    }
    if (!acc[email][monthYear]) {
      acc[email][monthYear] = [];
    }
    acc[email][monthYear].push(call);
    return acc;
  }, {});

  // Fetch users for dropdown
  const fetchUsersForDropdown = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          email: data.email,
          displayName: data.displayName || 'Unknown User',
        };
      });

      // Sort the usersList alphabetically by displayName
      usersList.sort((a, b) => a.displayName.localeCompare(b.displayName));

      setUsersList(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch user entries based on selected user
  const fetchUserEntries = async (email) => {
    try {
      console.log(`Fetching entries for email: ${email}`); // Debugging log
      const callsSnapshot = await getDocs(collection(db, 'hvac', 'CallData', 'Responses'));
      const skillsSnapshot = await getDocs(collection(db, 'hvac', 'SkillData', 'Responses'));
      const classesSnapshot = await getDocs(collection(db, 'hvac', 'ClassData', 'Responses'));
      const volunteerSnapshot = await getDocs(collection(db, 'hvac', 'VolunteerData', 'Responses'));

      const calls = [];
      const skills = [];
      const classes = [];
      const volunteerHours = [];

      callsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.Email.toLowerCase() === email.toLowerCase()) {  // Ensure case-insensitive comparison
          calls.push(data);
          console.log('Call entry found:', data); // Debugging log
        }
      });
      skillsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.Email.toLowerCase() === email.toLowerCase()) {  // Ensure case-insensitive comparison
          skills.push(data);
          console.log('Skill entry found:', data); // Debugging log
        }
      });
      classesSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.Email.toLowerCase() === email.toLowerCase()) {  // Ensure case-insensitive comparison
          classes.push(data);
          console.log('Class entry found:', data); // Debugging log
        }
      });
      volunteerSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.Email.toLowerCase() === email.toLowerCase()) {  // Ensure case-insensitive comparison
          volunteerHours.push(data);
          console.log('Volunteer entry found:', data); // Debugging log
        }
      });

      setUserEntries({ calls, skills, classes, volunteerHours });
    } catch (error) {
      console.error('Error fetching user entries:', error);
    }
  };

  useEffect(() => {
    fetchUsersForDropdown(); // Fetch users on component mount
  }, []);

  const calculateYearlyTotals = (entries) => {
    const totals = { calls: 0, skills: 0, classes: 0, volunteerHours: 0 };

    entries.forEach(entry => {
      if (entry.SkillName) {
        totals.skills += 1;
      } else if (entry.ClassName) {
        totals.classes += 1;
      } else if (entry.Time) {
        totals.volunteerHours += 1;
      } else {
        totals.calls += 1;
      }
    });

    return totals;
  };

  // Filter entries by selected year
  const filterEntriesByYear = (entries, year) => {
    if (!year) return entries;
    return entries.filter(entry => {
      const date = parseDate(entry.Date);
      if (!date) return false;
      return date.getFullYear().toString() === year;
    });
  };

  // Get available years from userEntries
  const availableYears = useMemo(() => {
    const allEntries = [...userEntries.calls, ...userEntries.skills, ...userEntries.classes, ...userEntries.volunteerHours];
    const yearsSet = new Set();
    allEntries.forEach(entry => {
      const date = parseDate(entry.Date);
      if (date) {
        yearsSet.add(date.getFullYear().toString());
      }
    });
    const years = Array.from(yearsSet).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
    return years.length > 0 ? years : [new Date().getFullYear().toString()];
  }, [userEntries]);

  // Filter user entries by selected year
  const filteredUserEntries = useMemo(() => {
    return {
      calls: filterEntriesByYear(userEntries.calls, selectedMainReportYear),
      skills: filterEntriesByYear(userEntries.skills, selectedMainReportYear),
      classes: filterEntriesByYear(userEntries.classes, selectedMainReportYear),
      volunteerHours: filterEntriesByYear(userEntries.volunteerHours, selectedMainReportYear),
    };
  }, [userEntries, selectedMainReportYear]);

  const yearlyTotals = calculateYearlyTotals([...filteredUserEntries.calls, ...filteredUserEntries.skills, ...filteredUserEntries.classes, ...filteredUserEntries.volunteerHours]);

  const exportToCSV = () => {
    if (!selectedUser) {
      console.error('No user selected for export.');
      return;
    }

    const groupedEntries = groupEntriesByMonth([
      ...filteredUserEntries.calls.map(entry => ({ ...entry, Type: 'Call' })),
      ...filteredUserEntries.skills.map(entry => ({ ...entry, Type: 'Skill' })),
      ...filteredUserEntries.classes.map(entry => ({ ...entry, Type: 'Class' })),
      ...filteredUserEntries.volunteerHours.map(entry => ({ ...entry, Type: 'Volunteer' })),
    ]);

    const entries = Object.entries(groupedEntries).flatMap(([monthYear, entries]) => 
      entries.map(entry => ({ Month: monthYear, ...entry }))
    );

    const csv = Papa.unparse(entries, {
      header: true,
      columns: ['Month', 'Date', 'Type', 'Role', 'SkillName', 'ClassName', 'Time', 'Activity'],
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'selected_user_report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to fetch user data and create a mapping of email to display name
  const fetchUserDisplayNames = async () => {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const displayNames = {};
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      const email = userData.email ? userData.email.toLowerCase() : null; // Check if email exists
      if (email) {
        displayNames[email] = userData.displayName || 'Unknown User'; // Map email to display name (case-insensitive)
      }
    });
    setUserDisplayNames(displayNames);
  };

  // Function to fetch user activities by month
  const fetchUserActivitiesByMonth = async () => {
    const userActivities = {}; // Object to hold activities grouped by month and user

    const collections = ['CallData', 'ClassData', 'SkillData', 'VolunteerData'];

    for (const collectionName of collections) {
      const snapshot = await getDocs(collection(db, 'hvac', collectionName, 'Responses')); // Accessing the Responses sub-collection
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const date = parseDate(data.Date); // Parse date correctly to avoid timezone issues
        if (!date) return; // Skip if date is invalid
        const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        const email = data.Email ? data.Email.toLowerCase() : null; // Convert email to lowercase for case-insensitive matching

        if (!userActivities[monthYear]) {
          userActivities[monthYear] = {}; // Initialize month if it doesn't exist
        }
        if (email && !userActivities[monthYear][email]) {
          userActivities[monthYear][email] = { activities: [], status: 'red' }; // Initialize user if it doesn't exist
        }

        // Push the activity data into the user's array for that month
        if (email) {
          userActivities[monthYear][email].activities.push({
            type: collectionName.replace('Data', ''), // Get the type (Call, Class, Skill, Volunteer)
            ...data, // Spread the rest of the data
          });
        }
      });
    }

    // Calculate status for each user based on their activities
    Object.entries(userActivities).forEach(([monthYear, users]) => {
      Object.entries(users).forEach(([email, userData]) => {
        const status = calculateUserStatus(userData.activities); // Calculate status based on activities
        userActivities[monthYear][email].status = status; // Assign status to user
      });
    });

    console.log("User Activities by Month:", userActivities); // Debugging log
    setUserActivities(userActivities); // Set the user activities state
  };

  // Function to calculate user status based on activities
  const calculateUserStatus = (activities) => {
    const metrics = getMonthlyMetrics(activities);
    return getStatusFromMetrics(metrics);
  };

  // Call this function in your useEffect or wherever appropriate
  useEffect(() => {
    const loadUserActivities = async () => {
      await fetchUserDisplayNames(); // Fetch user display names first
      await fetchUserActivitiesByMonth(); // Then fetch user activities
    };

    loadUserActivities();
  }, []);

  const currentCalendarYear = new Date().getFullYear().toString();

  const availableEligibilityYears = useMemo(() => {
    const yearsSet = new Set([currentCalendarYear]);
    Object.keys(userActivities).forEach(month => {
      const date = parseMonthYearToDate(month);
      if (date) {
        yearsSet.add(date.getFullYear().toString());
      }
    });
    return Array.from(yearsSet).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
  }, [userActivities, currentCalendarYear]);

  useEffect(() => {
    if (!availableEligibilityYears.length) {
      return;
    }
    if (!availableEligibilityYears.includes(selectedEligibilityYear)) {
      setSelectedEligibilityYear(availableEligibilityYears[0]);
    }
  }, [availableEligibilityYears, selectedEligibilityYear]);

  useEffect(() => {
    setOpenEligibilityUsers({});
  }, [selectedEligibilityYear]);

  const eligibilityMonths = useMemo(() => {
    if (!selectedEligibilityYear) {
      return [];
    }
    return generateMonthsForYear(selectedEligibilityYear);
  }, [selectedEligibilityYear]);

  const eligibilityUsers = useMemo(() => {
    if (!eligibilityMonths.length) {
      return [];
    }

    const monthsCount = eligibilityMonths.length;
    const isCurrentYearView = selectedEligibilityYear === currentCalendarYear;
    const uniqueEmails = new Set();

    eligibilityMonths.forEach(month => {
      Object.keys(userActivities[month] || {}).forEach(email => uniqueEmails.add(email));
    });

    const rows = Array.from(uniqueEmails).map(email => {
      const breakdown = eligibilityMonths.map((month, index) => {
        const status = userActivities[month]?.[email]?.status || 'none';
        return {
          monthIndex: index + 1,
          monthLabel: month,
          status,
        };
      });

      const greenCount = breakdown.filter(entry => entry.status === 'green').length;
      const isTenOfTwelve = isCurrentYearView && monthsCount >= 12 && greenCount >= 10;

      return {
        email,
        displayName: userDisplayNames[email] || email,
        greenCount,
        breakdown,
        isTenOfTwelve,
      };
    });

    return rows.sort((a, b) => {
      if (isCurrentYearView && a.isTenOfTwelve !== b.isTenOfTwelve) {
        return a.isTenOfTwelve ? -1 : 1;
      }
      if (b.greenCount !== a.greenCount) {
        return b.greenCount - a.greenCount;
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }, [eligibilityMonths, userActivities, userDisplayNames, selectedEligibilityYear, currentCalendarYear]);

  const lastSixEligibilityMonths = useMemo(() => {
    if (!eligibilityMonths.length) return [];
    return eligibilityMonths.slice(-6);
  }, [eligibilityMonths]);

  const sixMonthUsers = useMemo(() => {
    if (!lastSixEligibilityMonths.length) return [];

    // Get all unique emails from both userActivities and userDisplayNames
    const uniqueEmails = new Set(Object.keys(userDisplayNames));
    lastSixEligibilityMonths.forEach(month => {
      Object.keys(userActivities[month] || {}).forEach(email => uniqueEmails.add(email));
    });

    const rows = Array.from(uniqueEmails).map(email => {
      const breakdown = lastSixEligibilityMonths.map((month, idx) => {
        const status = userActivities[month]?.[email]?.status || 'none';
        return {
          month,
          index: idx + 1,
          status,
        };
      });

      const greenCount = breakdown.filter(entry => entry.status === 'green').length;

      return {
        email,
        displayName: userDisplayNames[email] || email,
        greenCount,
        monthsConsidered: lastSixEligibilityMonths.length,
        breakdown,
      };
    });

    return rows.sort((a, b) => {
      // Sort 5+ green to top
      const aIsFivePlus = a.greenCount >= 5;
      const bIsFivePlus = b.greenCount >= 5;
      if (aIsFivePlus !== bIsFivePlus) {
        return aIsFivePlus ? -1 : 1;
      }
      if (b.greenCount !== a.greenCount) return b.greenCount - a.greenCount;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [lastSixEligibilityMonths, userActivities, userDisplayNames]);

  // Calculate leaderboard for current month
  const leaderboardData = useMemo(() => {
    if (!usersData || Object.keys(usersData).length === 0) {
      return [];
    }

    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear().toString();

    // Calculate calls per user for current month
    const userCallCounts = {};
    
    Object.values(usersData).forEach(user => {
      if (!user || !user.calls || !Array.isArray(user.calls)) {
        return;
      }
      
      const monthCalls = user.calls.filter(call => {
        if (!call || !call.Date) {
          return false;
        }
        
        // Handle date as string (YYYY-MM-DD format) - same approach as Dashboard
        const dateString = call.Date;
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month] = dateString.split('-');
          return month === currentMonth && year === currentYear;
        }
        
        // Fallback to parseDate for other formats
        const callDate = parseDate(call.Date);
        if (!callDate) {
          return false;
        }
        const callMonth = (callDate.getMonth() + 1).toString().padStart(2, '0');
        const callYear = callDate.getFullYear().toString();
        return callMonth === currentMonth && callYear === currentYear;
      });
      
      if (monthCalls.length > 0) {
        userCallCounts[user.email] = {
          email: user.email,
          displayName: user.displayName,
          callCount: monthCalls.length,
          calls: monthCalls
        };
      }
    });

    // Sort by call count (descending), then by name
    return Object.values(userCallCounts).sort((a, b) => {
      if (b.callCount !== a.callCount) {
        return b.callCount - a.callCount;
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }, [usersData]);

  // Function to sort users by status
  const sortUsersByStatus = (users) => {
    const sortedUsers = Object.entries(users).sort(([, a], [, b]) => {
      const statusOrder = { red: 1, yellow: 2, green: 3 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
    return sortedUsers;
  };

  // Function to pick a random winner from users with green status for a specific month
  const pickRandomWinner = (monthYear) => {
    const users = Object.entries(userActivities[monthYear] || {}).filter(([, userData]) => userData.status === 'green');
    if (users.length > 0) {
      const randomIndex = Math.floor(Math.random() * users.length);
      const winnerEmail = users[randomIndex][0]; // Get the email of the randomly selected user
      setWinnerEmail(winnerEmail); // Store the winner's email
      setShowWinner(true); // Show the winner display
      cycleThroughNames(users); // Start cycling through names
    } else {
      alert('No users with green status for this month.'); // Alert if no green users are found
    }
  };

  // Function to cycle through names
  const cycleThroughNames = (users) => {
    let index = 0;
    const interval = setInterval(() => {
      setDisplayName(userDisplayNames[users[index][0]] || 'Unknown User'); // Set the display name
      index = (index + 1) % users.length; // Cycle through the users
    }, 200); // Change name every 200ms

    // Stop cycling after 5 seconds and set the final winner
    setTimeout(() => {
      clearInterval(interval);
      const finalWinnerEmail = users[Math.floor(Math.random() * users.length)][0]; // Final winner selection
      setWinner(userDisplayNames[finalWinnerEmail] || finalWinnerEmail); // Set the final winner's display name
      setDisplayName(userDisplayNames[finalWinnerEmail] || finalWinnerEmail); // Set the final display name
    }, 5000); // Cycle for 5 seconds
  };

  // Add this new component for the pie chart
  const CallRolesPieChart = ({ calls }) => {
    // Process the data for the pie chart
    const roleData = calls.reduce((acc, call) => {
      const role = call.Role || 'Unspecified';
      const existingRole = acc.find(item => item.name === role);
      if (existingRole) {
        existingRole.value += 1;
      } else {
        acc.push({ name: role, value: 1 });
      }
      return acc;
    }, []);

    // Colors for the pie chart segments
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

    return (
      <Box sx={{ width: '100%', height: 300 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={roleData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {roleData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // Add this function to handle pie chart display
  const handleShowPieChart = (monthYear, entries) => {
    const calls = entries.filter(entry => !entry.SkillName && !entry.ClassName && !entry.Time);
    setSelectedMonthCalls(calls);
    setShowPieChart(true);
  };

  if (userRole !== 'admin' && userRole !== 'ops' && userRole !=='recruiting') {
    return <Typography variant="h6" color="error">Access Denied</Typography>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        display: 'flex', 
        paddingTop: { xs: '56px', sm: '64px' }, 
        flexDirection: 'column', 
        padding: { xs: 1, sm: 2 },
        maxWidth: '100%',
        margin: '0 auto',
        width: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        '& > *': {
          maxWidth: '100%',
          overflow: 'hidden'
        }
      }}>
        {/* Tabs for navigation */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: { xs: 2, sm: 4 },
          overflowX: 'auto',
          '& .MuiTabs-scrollButtons': {
            display: { xs: 'flex', sm: 'none' }
          }
        }}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange} 
            variant={isSmallScreen ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              width: '100%',
              '& .MuiTab-root': {
                minWidth: { xs: 100, sm: 120 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '12px 8px', sm: '12px 16px' }
              }
            }}
          >
            <Tab label="Main Report" />
            <Tab label="Class Reports" />
            <Tab label="User Activities" />
            <Tab label="Green Eligibility" />
            <Tab label="Leaderboard" />
          </Tabs>
        </Box>

        {/* Main Report Content */}
        {selectedTab === 0 && (
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1000px', 
            margin: '0 auto',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#fff',
            borderRadius: { xs: 1, sm: 2 },
            boxShadow: 1
          }}>
            <Typography variant="h4" sx={{ 
              mb: { xs: 2, sm: 4 }, 
              textAlign: 'center',
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}>Reports Dashboard</Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2, 
              mb: 4,
              alignItems: { xs: 'stretch', sm: 'center' }
            }}>
              <Autocomplete
                options={usersList}
                getOptionLabel={(option) => option.displayName || ''}
                value={selectedUser}
                onChange={(event, newValue) => {
                  setSelectedUser(newValue);
                  if (newValue) {
                    fetchUserEntries(newValue.email);
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select a user" 
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                )}
                sx={{ flex: 1, minWidth: { xs: '100%', sm: '300px' } }}
              />
              
              {selectedUser && (
                <FormControl 
                  variant="outlined" 
                  sx={{ 
                    minWidth: { xs: '100%', sm: '150px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedMainReportYear}
                    onChange={(e) => setSelectedMainReportYear(e.target.value)}
                    label="Year"
                  >
                    {availableYears.map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
              {topResponders.map((responder, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <TopResponderCard rank={index + 1} user={responder} />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography variant="h5" sx={{ 
                mb: { xs: 2, sm: 3 }, 
                textAlign: 'center',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}>Yearly Totals</Typography>
              <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard 
                    title="Total Calls" 
                    value={yearlyTotals.calls}
                    icon={<PhoneInTalkIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main' }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard 
                    title="Total Skills" 
                    value={yearlyTotals.skills}
                    icon={<BuildIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main' }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard 
                    title="Total Classes" 
                    value={yearlyTotals.classes}
                    icon={<SchoolIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main' }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard 
                    title="Volunteer Hours" 
                    value={yearlyTotals.volunteerHours}
                    icon={<VolunteerActivismIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main' }} />}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography variant="h5" sx={{ 
                mb: { xs: 2, sm: 3 }, 
                textAlign: 'center',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}>User Entries</Typography>
              {selectedUser && (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    boxShadow: 3, 
                    borderRadius: { xs: 1, sm: 2 },
                    overflow: 'auto',
                    maxWidth: '100%',
                    '& .MuiTableCell-root': {
                      padding: { xs: '8px 4px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }
                  }}
                  className="mobile-table-container"
                >
                  <Table className="mobile-table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          backgroundColor: '#f5f5f5',
                          width: { xs: '40%', sm: '30%' },
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}>Month</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          backgroundColor: '#f5f5f5',
                          width: { xs: '60%', sm: '70%' },
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(groupEntriesByMonth([...filteredUserEntries.calls, ...filteredUserEntries.skills, ...filteredUserEntries.classes, ...filteredUserEntries.volunteerHours])).map(([monthYear, entries]) => {
                        const monthStatus = calculateMonthStatus(entries);
                        const monthColor = monthStatus === 'green' ? '#4caf50' : monthStatus === 'yellow' ? '#ff9800' : '#f44336';
                        const totalPoints = calculateTotalPoints(entries);
                        const statusLabel = monthStatus.charAt(0).toUpperCase() + monthStatus.slice(1);

                        return (
                          <React.Fragment key={monthYear}>
                            <TableRow sx={{ 
                              '&:hover': { 
                                backgroundColor: '#fafafa',
                                cursor: 'pointer'
                              }
                            }}>
                              <TableCell 
                                onClick={() => handleMonthToggle(monthYear)}
                                sx={{ 
                                  fontWeight: 'bold',
                                  color: monthColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: { xs: 0.5, sm: 1 },
                                  cursor: 'pointer',
                                  fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                              >
                                <IconButton
                                  size="small"
                                  sx={{ 
                                    color: monthColor,
                                    padding: { xs: '4px', sm: '8px' },
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                  }}
                                >
                                  {openMonths[monthYear] ? <KeyboardArrowUpIcon fontSize={isSmallScreen ? 'small' : 'medium'} /> : <KeyboardArrowDownIcon fontSize={isSmallScreen ? 'small' : 'medium'} />}
                                </IconButton>
                                <Box sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: { xs: '120px', sm: 'none' }
                                }}>
                                  {monthYear}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 } }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    gap: { xs: 1, sm: 2 }, 
                                    flexWrap: 'wrap',
                                    alignItems: 'center'
                                  }}>
                                    <Chip 
                                      label={statusLabel}
                                      color={monthStatus === 'green' ? 'success' : monthStatus === 'yellow' ? 'warning' : 'error'}
                                      sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        height: { xs: '24px', sm: '32px' }
                                      }}
                                      size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                    <Chip 
                                      label={`Points: ${totalPoints}`}
                                      variant="outlined"
                                      icon={<EmojiEventsIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />}
                                      sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        height: { xs: '24px', sm: '32px' }
                                      }}
                                      size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                    <Chip 
                                      label={`Calls: ${entries.filter(entry => !entry.SkillName && !entry.ClassName && !entry.Time).length}`}
                                      variant="outlined"
                                      icon={<LocalHospitalIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />}
                                      sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        height: { xs: '24px', sm: '32px' },
                                        '& .MuiChip-icon': {
                                          color: 'primary.main'
                                        }
                                      }}
                                      size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                    <Chip 
                                      label={`Skills: ${entries.filter(entry => entry.SkillName).length}`}
                                      variant="outlined"
                                      icon={<BuildIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />}
                                      sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        height: { xs: '24px', sm: '32px' },
                                        '& .MuiChip-icon': {
                                          color: 'primary.main'
                                        }
                                      }}
                                      size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                    <Chip 
                                      label={`Classes: ${entries.filter(entry => entry.ClassName).length}`}
                                      variant="outlined"
                                      icon={<SchoolIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />}
                                      sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        height: { xs: '24px', sm: '32px' },
                                        '& .MuiChip-icon': {
                                          color: 'primary.main'
                                        }
                                      }}
                                      size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                    <Chip 
                                      label={`Volunteer: ${entries.filter(entry => entry.Time).length}`}
                                      variant="outlined"
                                      icon={<VolunteerActivismIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />}
                                      sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        height: { xs: '24px', sm: '32px' },
                                        '& .MuiChip-icon': {
                                          color: 'primary.main'
                                        }
                                      }}
                                      size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ p: 0 }}>
                                <Collapse in={openMonths[monthYear]} timeout="auto" unmountOnExit>
                                  <Box sx={{ 
                                    p: { xs: 1, sm: 2 }, 
                                    backgroundColor: '#fafafa',
                                    borderTop: '1px solid #e0e0e0'
                                  }}>
                                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                                      {/* Calls Section */}
                                      {entries.filter(entry => !entry.SkillName && !entry.ClassName && !entry.Time).length > 0 && (
                                        <Grid item xs={12}>
                                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#fff' }}>
                                            <Box sx={{ 
                                              display: 'flex', 
                                              flexDirection: { xs: 'column', sm: 'row' },
                                              justifyContent: 'space-between', 
                                              alignItems: { xs: 'flex-start', sm: 'center' }, 
                                              mb: { xs: 1.5, sm: 2 },
                                              gap: { xs: 1, sm: 0 }
                                            }}>
                                              <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold', 
                                                color: 'primary.main',
                                                fontSize: { xs: '0.875rem', sm: '1rem' }
                                              }}>
                                                Calls
                                              </Typography>
                                              <Button
                                                variant="outlined"
                                                startIcon={<LocalHospitalIcon sx={{ fontSize: { xs: '16px', sm: '20px' } }} />}
                                                onClick={() => handleShowPieChart(monthYear, entries)}
                                                size={isSmallScreen ? 'small' : 'medium'}
                                                sx={{
                                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                  whiteSpace: 'nowrap'
                                                }}
                                              >
                                                View Distribution
                                              </Button>
                                            </Box>
                                            <TableContainer sx={{ maxHeight: { xs: '200px', sm: 'none' }, overflow: 'auto' }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' }
                                                    }}>Call Details</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {entries.filter(entry => !entry.SkillName && !entry.ClassName && !entry.Time).map((call, index) => (
                                                    <TableRow key={index} hover>
                                                      <TableCell sx={{ padding: { xs: '8px 4px', sm: '16px' } }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                          <Typography variant="body2" sx={{ 
                                                            fontWeight: 'bold',
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                          }}>
                                                            {formatDate(call.Date)}
                                                          </Typography>
                                                          <Typography variant="body2" sx={{ 
                                                            color: 'text.secondary', 
                                                            marginLeft: { xs: 1, sm: 2 },
                                                            fontStyle: 'italic',
                                                            fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                                          }}>
                                                            Role: {call.Role || 'N/A'}
                                                          </Typography>
                                                        </Box>
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* Skills Section */}
                                      {entries.filter(entry => entry.SkillName).length > 0 && (
                                        <Grid item xs={12}>
                                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#fff' }}>
                                            <Typography variant="subtitle1" sx={{ 
                                              mb: { xs: 1.5, sm: 2 }, 
                                              fontWeight: 'bold', 
                                              color: 'primary.main',
                                              fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}>
                                              Skills
                                            </Typography>
                                            <TableContainer sx={{ overflowX: 'auto' }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' },
                                                      whiteSpace: 'nowrap'
                                                    }}>Skill Name</TableCell>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' },
                                                      whiteSpace: 'nowrap'
                                                    }}>Date</TableCell>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' },
                                                      whiteSpace: 'nowrap'
                                                    }}>Practice Hours</TableCell>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' },
                                                      whiteSpace: 'nowrap'
                                                    }}>Practice Partner</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {entries.filter(entry => entry.SkillName).map((skill, index) => (
                                                    <TableRow key={index} hover>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{skill.SkillName}</TableCell>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{formatDate(skill.Date)}</TableCell>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{skill.PracticeHours || 'N/A'}</TableCell>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{skill.PracticePartner || 'N/A'}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* Classes Section */}
                                      {entries.filter(entry => entry.ClassName).length > 0 && (
                                        <Grid item xs={12}>
                                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#fff' }}>
                                            <Typography variant="subtitle1" sx={{ 
                                              mb: { xs: 1.5, sm: 2 }, 
                                              fontWeight: 'bold', 
                                              color: 'primary.main',
                                              fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}>
                                              Classes
                                            </Typography>
                                            <TableContainer sx={{ overflowX: 'auto' }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' }
                                                    }}>Class Name</TableCell>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' }
                                                    }}>Date</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {entries.filter(entry => entry.ClassName).map((classEntry, index) => (
                                                    <TableRow key={index} hover>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{classEntry.ClassName}</TableCell>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{formatDate(classEntry.Date)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Grid>
                                      )}

                                      {/* Volunteer Hours Section */}
                                      {entries.filter(entry => entry.Time).length > 0 && (
                                        <Grid item xs={12}>
                                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#fff' }}>
                                            <Typography variant="subtitle1" sx={{ 
                                              mb: { xs: 1.5, sm: 2 }, 
                                              fontWeight: 'bold', 
                                              color: 'primary.main',
                                              fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}>
                                              Volunteer Hours
                                            </Typography>
                                            <TableContainer sx={{ overflowX: 'auto' }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' }
                                                    }}>Date</TableCell>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' }
                                                    }}>Hours</TableCell>
                                                    <TableCell sx={{ 
                                                      fontWeight: 'bold',
                                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                      padding: { xs: '8px 4px', sm: '16px' }
                                                    }}>Activity</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {entries.filter(entry => entry.Time).map((volunteer, index) => (
                                                    <TableRow key={index} hover>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{formatDate(volunteer.Date)}</TableCell>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{volunteer.Time}</TableCell>
                                                      <TableCell sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        padding: { xs: '8px 4px', sm: '16px' }
                                                      }}>{volunteer.Activity}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={exportToCSV}
                sx={{ 
                  minWidth: 200,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Export to CSV
              </Button>
            </Box>
          </Box>
        )}

        {/* Green Eligibility Content */}
        {selectedTab === 3 && (
          <Box sx={{
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#fff',
            borderRadius: { xs: 1, sm: 2 },
            boxShadow: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 }
          }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: { xs: 1, sm: 2 }
              }}>
                <Typography variant="h5" sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  10-of-12 Month Green Tracker
                </Typography>
                <FormControl
                  size="small"
                  sx={{ minWidth: 160 }}
                  disabled={!availableEligibilityYears.length}
                >
                  <InputLabel id="eligibility-year-label">Year</InputLabel>
                  <Select
                    labelId="eligibility-year-label"
                    value={selectedEligibilityYear}
                    label="Year"
                    onChange={(event) => setSelectedEligibilityYear(event.target.value)}
                  >
                    {availableEligibilityYears.map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {eligibilityMonths.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: { xs: 1, sm: 2 } }}>
                  {eligibilityMonths.map((month, idx) => (
                    <Chip key={month} label={`${idx + 1}. ${month}`} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
              {!eligibilityMonths.length && (
                <Typography textAlign="center">
                  Select a year to view eligibility progress.
                </Typography>
              )}
              {eligibilityMonths.length > 0 && eligibilityUsers.length === 0 && (
                <Typography textAlign="center">
                  No activity recorded for {selectedEligibilityYear}.
                </Typography>
              )}
              {eligibilityUsers.map(user => {
                const isOpen = Boolean(openEligibilityUsers[user.email]);
                return (
                  <Card 
                    key={user.email} 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 2, 
                      boxShadow: 2,
                      border: user.isTenOfTwelve ? '2px solid #4caf50' : '1px solid rgba(0,0,0,0.08)'
                    }}
                  >
                    <CardContent>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 2
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{user.displayName}</Typography>
                            {user.isTenOfTwelve && (
                              <Chip 
                                color="success" 
                                size="small" 
                                label="10 of 12"
                                sx={{ fontWeight: 'bold' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            color={user.greenCount > 0 ? 'success' : 'default'}
                            label={`${user.greenCount} of ${eligibilityMonths.length} months green`}
                            sx={{ fontWeight: 'bold' }}
                          />
                          <IconButton onClick={() => handleEligibilityToggle(user.email)}>
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Box>
                      </Box>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <TableContainer sx={{
                          maxHeight: 320,
                          '& .MuiTableCell-root': {
                            padding: { xs: '6px 4px', sm: '10px 12px' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }
                        }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Month #</TableCell>
                                <TableCell>Month</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {user.breakdown.map(entry => {
                                const statusLabel = entry.status === 'none' ? 'No Data' : entry.status;
                                const color = entry.status === 'green'
                                  ? 'success'
                                  : entry.status === 'yellow'
                                    ? 'warning'
                                    : entry.status === 'red'
                                      ? 'error'
                                      : 'default';
                                return (
                                  <TableRow key={`${user.email}-${entry.monthLabel}`}>
                                    <TableCell>{entry.monthIndex}</TableCell>
                                    <TableCell>{entry.monthLabel}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
                                        color={color}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
            <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
              <Paper sx={{ 
                p: 2, 
                borderRadius: 2, 
                position: 'sticky', 
                top: 72,
                boxShadow: 2
              }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                  Green 5 of Last 6
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Using the last 6 months from the selected year:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                  {lastSixEligibilityMonths.length === 0 ? (
                    <Chip label="No months yet" size="small" variant="outlined" />
                  ) : (
                    lastSixEligibilityMonths.map((month, idx) => (
                      <Chip key={month} label={`${idx + 1}. ${month}`} size="small" variant="outlined" />
                    ))
                  )}
                </Box>
                {sixMonthUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No responders found for this period.
                  </Typography>
                )}
                <List dense>
                  {sixMonthUsers.map(user => (
                    <React.Fragment key={user.email}>
                      <ListItem sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 600 }}>
                                {user.displayName}
                              </Typography>
                              <Chip 
                                label={`${user.greenCount}/${user.monthsConsidered} green`}
                                color={user.greenCount >= 5 ? 'success' : 'default'}
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {user.email}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {lastSixEligibilityMonths.length === 0 ? (
                                  <Chip label="No months yet" size="small" variant="outlined" />
                                ) : (
                                  user.breakdown.map(({ month, status, index }) => {
                                    const color =
                                      status === 'green'
                                        ? 'success'
                                        : status === 'yellow'
                                          ? 'warning'
                                          : status === 'red'
                                            ? 'error'
                                            : 'default';
                                    const label = `${index}. ${month}`;
                                    return (
                                      <Chip 
                                        key={`${user.email}-${month}-${index}`} 
                                        label={label} 
                                        size="small" 
                                        color={color}
                                        variant={status === 'green' ? 'filled' : 'outlined'}
                                      />
                                    );
                                  })
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Leaderboard Content */}
        {selectedTab === 4 && (
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px', 
            margin: '0 auto',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#fff',
            borderRadius: { xs: 1, sm: 2 },
            boxShadow: 1
          }}>
            <Typography variant="h4" sx={{ 
              mb: { xs: 2, sm: 4 }, 
              textAlign: 'center',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <EmojiEventsIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
              Monthly Leaderboard - {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
            </Typography>

            {leaderboardData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No calls recorded for this month yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ 
                borderRadius: 2,
                boxShadow: 2,
                overflowX: 'auto'
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>Rank</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>Name</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>Email</TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>Calls</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboardData.map((user, index) => {
                      const rank = index + 1;
                      const isTopThree = rank <= 3;
                      
                      // Helper function to get background color
                      const getBackgroundColor = (opacity) => {
                        if (rank === 1) return `rgba(255, 215, 0, ${opacity})`;
                        if (rank === 2) return `rgba(192, 192, 192, ${opacity})`;
                        if (rank === 3) return `rgba(205, 127, 50, ${opacity})`;
                        return `rgba(0, 0, 0, ${opacity})`;
                      };
                      
                      return (
                        <TableRow 
                          key={user.email}
                          sx={{ 
                            backgroundColor: isTopThree ? getBackgroundColor(0.1) : 'inherit',
                            '&:hover': { 
                              backgroundColor: isTopThree ? getBackgroundColor(0.2) : 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <TableCell sx={{ 
                            fontWeight: isTopThree ? 'bold' : 'normal',
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {rank === 1 && <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: '1.5rem' }} />}
                              {rank === 2 && <EmojiEventsIcon sx={{ color: '#C0C0C0', fontSize: '1.5rem' }} />}
                              {rank === 3 && <EmojiEventsIcon sx={{ color: '#CD7F32', fontSize: '1.5rem' }} />}
                              <Typography component="span" sx={{ 
                                fontWeight: isTopThree ? 'bold' : 'normal',
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }}>
                                #{rank}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: isTopThree ? 'bold' : 'normal',
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}>{user.displayName}</TableCell>
                          <TableCell sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            color: 'text.secondary'
                          }}>{user.email}</TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: isTopThree ? 'bold' : 'normal',
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}>
                            <Chip 
                              label={user.callCount} 
                              color={isTopThree ? 'primary' : 'default'}
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {leaderboardData.length > 0 && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {leaderboardData.length} {leaderboardData.length === 1 ? 'responder' : 'responders'} with calls this month
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* User Activities Content */}
        {selectedTab === 2 && (
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px', 
            margin: '0 auto',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#fff',
            borderRadius: { xs: 1, sm: 2 },
            boxShadow: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 }
          }}>
            <Typography variant="h5" sx={{ 
              mb: { xs: 2, sm: 4 }, 
              textAlign: 'center',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}>User Activities Report</Typography>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(userActivities).map(([monthYear, users]) => {
              const statusTally = Object.values(users).reduce((acc, userData) => {
                acc[userData.status] = (acc[userData.status] || 0) + 1;
                return acc;
              }, { green: 0, yellow: 0, red: 0 });
              const totalUsers = Object.keys(users).length;
              return (
                <Card key={monthYear} sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' }, 
                      justifyContent: 'space-between', 
                      gap: 2,
                      alignItems: { xs: 'flex-start', sm: 'center' }
                    }}>
                      <Box>
                        <Typography variant="h5" sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 'bold' }} onClick={() => handleMonthToggle(monthYear)}>
                          {monthYear}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {totalUsers} {totalUsers === 1 ? 'Responder' : 'Responders'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip label={`Green: ${statusTally.green}`} color="success" size="small" />
                        <Chip label={`Yellow: ${statusTally.yellow}`} color="warning" size="small" />
                        <Chip label={`Red: ${statusTally.red}`} color="error" size="small" />
                        <Button variant="contained" size="small" onClick={() => pickRandomWinner(monthYear)}>
                          Pick Random Winner
                        </Button>
                      </Box>
                    </Box>
                    <Collapse in={openMonths[monthYear]} timeout="auto" unmountOnExit>
                      <TableContainer sx={{ 
                        mt: 2,
                        overflowX: 'auto',
                        '& .MuiTableCell-root': {
                          padding: { xs: '8px 6px', sm: '12px 16px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Responder</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="center">Calls</TableCell>
                              <TableCell align="center">Skills</TableCell>
                              <TableCell align="center">Classes</TableCell>
                              <TableCell align="center">Volunteer</TableCell>
                              <TableCell align="center">Last Activity</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortUsersByStatus(users).map(([email, userData]) => {
                              const summary = summarizeActivities(userData.activities);
                              const isUserOpen = openUsers[`${monthYear}-${email}`];
                              const statusColor = userData.status === 'green' ? 'success' : userData.status === 'yellow' ? 'warning' : 'error';
                              return (
                                <React.Fragment key={email}>
                                  <TableRow hover>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton size="small" onClick={() => handleUserToggle(monthYear, email)}>
                                          {isUserOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                        </IconButton>
                                        <Box>
                                          <Typography sx={{ fontWeight: 600 }}>
                                            {userDisplayNames[email] || email}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {email}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={userData.status.charAt(0).toUpperCase() + userData.status.slice(1)} 
                                        color={statusColor}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell align="center">{summary.calls}</TableCell>
                                    <TableCell align="center">{summary.skills}</TableCell>
                                    <TableCell align="center">{summary.classes}</TableCell>
                                    <TableCell align="center">{summary.volunteer}</TableCell>
                                    <TableCell align="center">
                                      {summary.lastActivity ? formatDate(summary.lastActivity) : 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={7} sx={{ p: 0, backgroundColor: '#fafafa' }}>
                                      <Collapse in={isUserOpen} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Activity Detail
                                          </Typography>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Notes</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {userData.activities.map((activity, index) => (
                                                <TableRow key={index}>
                                                  <TableCell>{activity.type}</TableCell>
                                                  <TableCell>{formatDate(activity.Date)}</TableCell>
                                                  <TableCell>
                                                    {activity.type === 'Call' && `Role: ${activity.Role || 'N/A'}`}
                                                    {activity.type === 'Skill' && activity.SkillName}
                                                    {activity.type === 'Class' && activity.ClassName}
                                                    {activity.type === 'Volunteer' && activity.Activity}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
            </Box>
            <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
              <Paper sx={{ 
                p: 2, 
                borderRadius: 2, 
                position: 'sticky', 
                top: 72,
                boxShadow: 2
              }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                  Green 5 of Last 6
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Auto-calculated across the most recent six months.
                </Typography>
                {sixMonthUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No responders meet this threshold yet.
                  </Typography>
                )}
                <List dense>
                  {sixMonthUsers.map(user => (
                    <React.Fragment key={user.email}>
                      <ListItem sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontWeight: 600 }}>
                                {user.displayName}
                              </Typography>
                              <Chip 
                                label={`${user.greenCount}/${user.monthsConsidered} green`}
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Class Reports Content */}
        {selectedTab === 1 && (
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1000px', 
            margin: '0 auto',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#fff',
            borderRadius: { xs: 1, sm: 2 },
            boxShadow: 1
          }}>
            <Typography variant="h5" sx={{ 
              mb: { xs: 2, sm: 4 }, 
              textAlign: 'center',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}>Class Reports</Typography>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: 3, 
                borderRadius: { xs: 1, sm: 2 },
                overflowX: 'auto',
                '& .MuiTableCell-root': {
                  padding: { xs: '8px 4px', sm: '16px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}>Class Name</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}>Users with Entries</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(classReports).map(([className, userEmails]) => (
                    <React.Fragment key={className}>
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => setOpenClasses(prev => ({ ...prev, [className]: !prev[className] }))}
                            >
                              {openClasses[className] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                            {className}
                          </Box>
                        </TableCell>
                        <TableCell>{userEmails.length} {userEmails.length === 1 ? 'User' : 'Users'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={openClasses[className]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              {userEmails.length > 0 ? userEmails.join(', ') : 'No entries'}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Winner Display with Confetti */}
        {showWinner && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            zIndex: 1000, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)'
          }}>
            <Confetti />
            <Typography variant="h3" sx={{ mb: 3 }}>🎉 Congratulations! 🎉</Typography>
            <Typography variant="h4" sx={{ mb: 4 }}>{displayName}</Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              onClick={() => setShowWinner(false)}
              sx={{ 
                minWidth: 200,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              Close
            </Button>
          </Box>
        )}

        {/* Snackbar to show the winner */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={`Random Winner: ${winner}`}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        {/* Dialog component for the pie chart */}
        <Dialog
          open={showPieChart}
          onClose={() => setShowPieChart(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              Call Role Distribution
            </Typography>
          </DialogTitle>
          <DialogContent>
            <CallRolesPieChart calls={selectedMonthCalls} />
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default Reports;
